import React, { useState, useEffect } from "react";
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import Dropdown from "../../../components/Dropdown";
import Dialog from "../../../components/Dialog";
import Swal from "sweetalert2";
import {
  ONLY_ALPHABETS,
  testRegex,
  NUMBERS_ONLY,
  NUMBERS_DOT,
} from "../../../config/regex";

const COLOR_OPTIONS = [
  { value: "from-primary-400 to-primary-600", label: "Primary (Green)" },
  { value: "from-secondary-400 to-secondary-600", label: "Secondary (Green)" },
  { value: "from-blue-400 to-blue-600", label: "Blue" },
  { value: "from-green-400 to-green-600", label: "Green" },
  { value: "from-purple-400 to-purple-600", label: "Purple" },
  { value: "from-indigo-400 to-indigo-600", label: "Indigo" },
  { value: "from-amber-400 to-amber-600", label: "Amber" },
  { value: "from-rose-400 to-rose-600", label: "Rose" },
  { value: "from-orange-400 to-orange-600", label: "Orange" },
];

const TREND_OPTIONS = [
  { value: "Growing", label: "↑ Growing" },
  { value: "Stable", label: "→ Stable" },
  { value: "Declining", label: "↓ Declining" },
];

const AddBroker = ({ open, onClose, onSuccess, editBroker, brokerDetails }) => {
  const [form, setForm] = useState({
    name: "",
    logo: "📊",
    color: "from-primary-400 to-primary-600",
    rating: "",
    activeClients: "",
    activeClientsTrend: "Growing",
    brokerage: "",
    description: "",
    features: "",
    delivery: "",
    intraday: "",
    futures: "",
    complaintsTotal: "",
    complaintsResolved: "",
    promoters: "",
    public: "",
    pros: "",
    cons: "",
    revenue: "",
    profit: "",
    marketCap: "",
  });
  const [initialForm, setInitialForm] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const isInvalid = React.useMemo(() => {
    const hasErrors = Object.keys(errors).length > 0;
    const requiredFields = [
      "name",
      "logo",
      "rating",
      "activeClients",
      "brokerage",
      "description",
      "features",
      "delivery",
      "intraday",
      "futures",
      "complaintsTotal",
      "complaintsResolved",
      "promoters",
      "public",
      "pros",
      "cons",
      "revenue",
      "profit",
      "marketCap",
    ];
    const hasEmptyFields = requiredFields.some(
      (field) => !String(form[field] || "").trim(),
    );
    return hasErrors || hasEmptyFields;
  }, [errors, form]);

  const isChanged = React.useMemo(() => {
    return JSON.stringify(form) !== JSON.stringify(initialForm);
  }, [form, initialForm]);

  const isEdit = Boolean(editBroker?.id);

  useEffect(() => {
    if (!open) return;
    let data = {};
    if (editBroker) {
      const d = brokerDetails[editBroker.name] || {};
      data = {
        name: editBroker.name || "",
        logo: editBroker.logo || "📊",
        color: editBroker.color || "from-primary-400 to-primary-600",
        rating: d.rating != null ? String(d.rating) : "",
        activeClients: d.activeClients || "",
        activeClientsTrend: d.activeClientsTrend || "Growing",
        brokerage: d.brokerage || "",
        description: d.description || "",
        features: Array.isArray(d.features)
          ? d.features.join(", ")
          : d.features || "",
        delivery: (d.charges && d.charges.delivery) || "",
        intraday: (d.charges && d.charges.intraday) || "",
        futures: (d.charges && d.charges.futures) || "",
        complaintsTotal:
          (d.complaints && d.complaints.total) != null
            ? String(d.complaints.total)
            : "",
        complaintsResolved:
          (d.complaints && d.complaints.resolved) != null
            ? String(d.complaints.resolved)
            : "",
        promoters: (d.shareHolding && d.shareHolding.promoters) || "",
        public: (d.shareHolding && d.shareHolding.public) || "",
        pros: Array.isArray(d.pros) ? d.pros.join("\n") : d.pros || "",
        cons: Array.isArray(d.cons) ? d.cons.join("\n") : d.cons || "",
        revenue: (d.financials && d.financials.revenue) || "",
        profit: (d.financials && d.financials.profit) || "",
        marketCap: (d.financials && d.financials.marketCap) || "",
      };
    } else {
      data = {
        name: "",
        logo: "📊",
        color: "from-primary-400 to-primary-600",
        rating: "",
        activeClients: "",
        activeClientsTrend: "Growing",
        brokerage: "",
        description: "",
        features: "",
        delivery: "",
        intraday: "",
        futures: "",
        complaintsTotal: "",
        complaintsResolved: "",
        promoters: "",
        public: "",
        pros: "",
        cons: "",
        revenue: "",
        profit: "",
        marketCap: "",
      };
    }
    setForm(data);
    setInitialForm(data);
    setErrors({});
    setTouched({});
  }, [open, editBroker, brokerDetails]);

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const percentFields = ["delivery", "intraday", "futures", "promoters", "public"];
    if (percentFields.includes(field)) {
      setForm((prev) => {
        const val = String(prev[field] || "").trim();
        if (val && !val.endsWith("%")) {
          return { ...prev, [field]: val + "%" };
        }
        return prev;
      });
    }
  };

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) {
      e.name = "Broker name is required";
    } else if (!testRegex(ONLY_ALPHABETS, form.name)) {
      e.name = "Broker name must contain only alphabets";
    }

    if (!form.logo.trim()) e.logo = "Logo is required";

    if (!form.rating.trim()) {
      e.rating = "Rating is required";
    } else if (!testRegex(NUMBERS_ONLY, form.rating)) {
      e.rating = "Rating must be an integer";
    } else {
      const r = parseInt(form.rating, 10);
      if (r < 0 || r > 5) {
        e.rating = "Rating must be between 0 and 5";
      }
    }

    if (!form.activeClients.trim()) {
      e.activeClients = "Active clients is required";
    }

    if (!form.brokerage.trim()) {
      e.brokerage = "Brokerage is required";
    }

    if (!form.description.trim()) e.description = "Description is required";
    if (!form.features.trim()) e.features = "Features are required";

    if (!form.delivery.trim()) e.delivery = "Delivery charge is required";
    if (!form.intraday.trim()) e.intraday = "Intraday charge is required";
    if (!form.futures.trim()) e.futures = "Futures charge is required";

    if (!form.complaintsTotal.trim()) {
      e.complaintsTotal = "Total complaints is required";
    } else if (!testRegex(NUMBERS_ONLY, form.complaintsTotal)) {
      e.complaintsTotal = "Total complaints must be a number";
    }

    if (!form.complaintsResolved.trim()) {
      e.complaintsResolved = "Resolved complaints is required";
    } else if (!testRegex(NUMBERS_ONLY, form.complaintsResolved)) {
      e.complaintsResolved = "Resolved complaints must be a number";
    }

    if (!form.promoters.trim()) e.promoters = "Promoters % is required";
    if (!form.public.trim()) e.public = "Public % is required";

    if (!form.pros.trim()) e.pros = "Pros are required";
    if (!form.cons.trim()) e.cons = "Cons are required";

    if (!form.revenue.trim()) e.revenue = "Revenue is required";
    if (!form.profit.trim()) e.profit = "Profit is required";
    if (!form.marketCap.trim()) e.marketCap = "Market Cap is required";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  useEffect(() => {
    validate();
  }, [form]);

  const handleSave = () => {
    if (!validate()) return;
    const name = form.name.trim();
    const detailsPayload = {
      rating: form.rating ? parseFloat(form.rating) : 4,
      activeClients: form.activeClients.trim() || "—",
      activeClientsTrend: form.activeClientsTrend || "Growing",
      brokerage: form.brokerage.trim() || "—",
      description: form.description.trim() || "Leading stock broker.",
      features: form.features.trim()
        ? form.features
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      charges: {
        delivery: form.delivery.trim() || "0%",
        intraday: form.intraday.trim() || "0.03%",
        futures: form.futures.trim() || "0.03%",
      },
      complaints: {
        total: form.complaintsTotal.trim()
          ? parseInt(form.complaintsTotal, 10)
          : 89,
        resolved: form.complaintsResolved.trim()
          ? parseInt(form.complaintsResolved, 10)
          : 85,
      },
      shareHolding: {
        promoters: form.promoters.trim() || "52%",
        public: form.public.trim() || "48%",
      },
      pros: form.pros.trim()
        ? form.pros
            .split(/\n/)
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      cons: form.cons.trim()
        ? form.cons
            .split(/\n/)
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      financials: {
        revenue: form.revenue.trim() || "₹3,200 Cr",
        profit: form.profit.trim() || "₹1,200 Cr",
        marketCap: form.marketCap.trim() || "₹28,000 Cr",
      },
    };
    onSuccess?.(
      {
        id: editBroker?.id,
        name,
        logo: form.logo.trim() || "📊",
        color: form.color,
      },
      detailsPayload,
      isEdit ? editBroker.name : null,
    );
    setErrors({});
    onClose();
  };

  const footer = (
    <>
      <Button variant="secondary" size="sm" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        type="submit"
        form="broker-form"
        disabled={isInvalid || !isChanged}
      >
        {isEdit ? "Update Changes" : "Add Broker"}
      </Button>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Broker" : "Add Broker"}
      size="lg"
      footer={footer}
    >
      <form
        id="broker-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextInput
            name="name"
            label="Broker name"
            placeholder="e.g. Zerodha"
            value={form.name}
            onChange={(e) => handleChange("name", e.target.value)}
            onBlur={() => handleBlur("name")}
            error={touched.name ? errors.name : ""}
            disabled={isEdit}
            required
          />
          <TextInput
            name="logo"
            label="Logo (emoji)"
            placeholder="📊"
            value={form.logo}
            onChange={(e) => handleChange("logo", e.target.value)}
            onBlur={() => handleBlur("logo")}
            error={touched.logo ? errors.logo : ""}
            required
          />
          <div className="sm:col-span-2">
            <Dropdown
              name="color"
              label="Theme color"
              value={form.color}
              onChange={(e) => handleChange("color", e.target.value)}
              onBlur={() => handleBlur("color")}
              options={COLOR_OPTIONS}
              placeholder="Select color"
              error={touched.color ? errors.color : ""}
              required
            />
          </div>
          <TextInput
            name="rating"
            label="Rating (0–5)"
            placeholder="5"
            value={form.rating}
            onChange={(e) => handleChange("rating", e.target.value)}
            onBlur={() => handleBlur("rating")}
            error={touched.rating ? errors.rating : ""}
            required
          />
          <TextInput
            name="activeClients"
            label="Active clients"
            placeholder="e.g. 80L+"
            value={form.activeClients}
            onChange={(e) => handleChange("activeClients", e.target.value)}
            onBlur={() => handleBlur("activeClients")}
            error={touched.activeClients ? errors.activeClients : ""}
            required
          />
          <Dropdown
            name="activeClientsTrend"
            label="Active clients trend"
            value={form.activeClientsTrend}
            onChange={(e) => handleChange("activeClientsTrend", e.target.value)}
            onBlur={() => handleBlur("activeClientsTrend")}
            options={TREND_OPTIONS}
            placeholder="Trend"
            error={touched.activeClientsTrend ? errors.activeClientsTrend : ""}
            required
          />
          <TextInput
            name="brokerage"
            label="Brokerage (summary)"
            placeholder="e.g. 0.03%"
            value={form.brokerage}
            onChange={(e) => handleChange("brokerage", e.target.value)}
            onBlur={() => handleBlur("brokerage")}
            error={touched.brokerage ? errors.brokerage : ""}
            required
          />
        </div>

        <div className="mt-4">
          <TextInput
            name="description"
            label="Description"
            placeholder="Short description of the broker"
            value={form.description}
            onChange={(e) => handleChange("description", e.target.value)}
            onBlur={() => handleBlur("description")}
            error={touched.description ? errors.description : ""}
            multiline
            rows={2}
            required
          />
        </div>
        <div className="mt-4">
          <TextInput
            name="features"
            label="Features (comma-separated)"
            placeholder="Zero Brokerage, Kite Platform, Research Tools"
            value={form.features}
            onChange={(e) => handleChange("features", e.target.value)}
            onBlur={() => handleBlur("features")}
            error={touched.features ? errors.features : ""}
            required
          />
        </div>

        <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">
          Charges
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextInput
            name="delivery"
            label="Delivery %"
            placeholder="0%"
            value={form.delivery}
            onChange={(e) => handleChange("delivery", e.target.value)}
            onBlur={() => handleBlur("delivery")}
            error={touched.delivery ? errors.delivery : ""}
            required
          />
          <TextInput
            name="intraday"
            label="Intraday %"
            placeholder="0.03%"
            value={form.intraday}
            onChange={(e) => handleChange("intraday", e.target.value)}
            onBlur={() => handleBlur("intraday")}
            error={touched.intraday ? errors.intraday : ""}
            required
          />
          <TextInput
            name="futures"
            label="Futures %"
            placeholder="0.03%"
            value={form.futures}
            onChange={(e) => handleChange("futures", e.target.value)}
            onBlur={() => handleBlur("futures")}
            error={touched.futures ? errors.futures : ""}
            required
          />
        </div>

        <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">
          Complaints
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextInput
            name="complaintsTotal"
            label="Total complaints"
            placeholder="89"
            value={form.complaintsTotal}
            onChange={(e) => handleChange("complaintsTotal", e.target.value)}
            onBlur={() => handleBlur("complaintsTotal")}
            error={touched.complaintsTotal ? errors.complaintsTotal : ""}
            required
          />
          <TextInput
            name="complaintsResolved"
            label="Resolved"
            placeholder="85"
            value={form.complaintsResolved}
            onChange={(e) => handleChange("complaintsResolved", e.target.value)}
            onBlur={() => handleBlur("complaintsResolved")}
            error={touched.complaintsResolved ? errors.complaintsResolved : ""}
            required
          />
        </div>

        <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">
          Share Holding
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextInput
            name="promoters"
            label="Promoters %"
            placeholder="52%"
            value={form.promoters}
            onChange={(e) => handleChange("promoters", e.target.value)}
            onBlur={() => handleBlur("promoters")}
            error={touched.promoters ? errors.promoters : ""}
            required
          />
          <TextInput
            name="public"
            label="Public %"
            placeholder="48%"
            value={form.public}
            onChange={(e) => handleChange("public", e.target.value)}
            onBlur={() => handleBlur("public")}
            error={touched.public ? errors.public : ""}
            required
          />
        </div>

        <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">
          Pros & Cons
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextInput
            name="pros"
            label="Pros (one per line)"
            placeholder="Zero brokerage on delivery\nAdvanced charting tools"
            value={form.pros}
            onChange={(e) => handleChange("pros", e.target.value)}
            onBlur={() => handleBlur("pros")}
            error={touched.pros ? errors.pros : ""}
            multiline
            rows={3}
            required
          />
          <TextInput
            name="cons"
            label="Cons (one per line)"
            placeholder="Limited branch network\nNo research reports"
            value={form.cons}
            onChange={(e) => handleChange("cons", e.target.value)}
            onBlur={() => handleBlur("cons")}
            error={touched.cons ? errors.cons : ""}
            multiline
            rows={3}
            required
          />
        </div>

        <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">
          Financials
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextInput
            name="revenue"
            label="Revenue"
            placeholder="₹3,200 Cr"
            value={form.revenue}
            onChange={(e) => handleChange("revenue", e.target.value)}
            onBlur={() => handleBlur("revenue")}
            error={touched.revenue ? errors.revenue : ""}
            required
          />
          <TextInput
            name="profit"
            label="Profit"
            placeholder="₹1,200 Cr"
            value={form.profit}
            onChange={(e) => handleChange("profit", e.target.value)}
            onBlur={() => handleBlur("profit")}
            error={touched.profit ? errors.profit : ""}
            required
          />
          <TextInput
            name="marketCap"
            label="Market Cap"
            placeholder="₹28,000 Cr"
            value={form.marketCap}
            onChange={(e) => handleChange("marketCap", e.target.value)}
            onBlur={() => handleBlur("marketCap")}
            error={touched.marketCap ? errors.marketCap : ""}
            required
          />
        </div>
      </form>
    </Dialog>
  );
};

export default AddBroker;
